import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import mixpanel from "mixpanel-browser";

import Button from "components/Button";

import { addTransactionToHistory } from "store/payments/thunk";
import { RUNS_PER_CREDIT } from "utils/parametric/constants";
import { TRANSACTIONS_TYPES, URL } from "utils/payment/constants";
import { useStateSelector } from "utils/field";

import classes from "./styles.module.scss";
import parentClasses from "../styles.module.scss";

const formatCount = (value) => Number(value || 0).toLocaleString();

const NewCredits = ({
    newTotalCredits = 0,
    onClose,
    spentId,
    spentName,
    creditsNeeded = 0,
    shortfallRuns = 0,
    availableRuns = 0,
    onCreditsToSpendChange,
    runsPerCredit = RUNS_PER_CREDIT,
    transactionType = TRANSACTIONS_TYPES["SPENT.PARAMETRIC"],
    spentFeature = "PARAMETRIC",
    spentUrl = URL.PARAMETRIC,
    analyticsLabel = "Parametric",
    subjectLabel = "analysis",
}) => {
    const dispatch = useDispatch();
    const userUid = useStateSelector("user.uid") || "";
    const minCredits = creditsNeeded > 0 ? creditsNeeded : 1;
    const [creditsToSpend, setCreditsToSpend] = useState(minCredits);
    const [isSpending, setIsSpending] = useState(false);
    const [spendError, setSpendError] = useState(null);

    useEffect(() => {
        setCreditsToSpend(minCredits);
    }, [minCredits]);

    useEffect(() => {
        onCreditsToSpendChange?.(creditsToSpend);
    }, [creditsToSpend, onCreditsToSpendChange]);

    const runsToUnlock = creditsToSpend * runsPerCredit;
    const projectedUnlocked = availableRuns + runsToUnlock;
    const hasEnoughCredits = newTotalCredits >= creditsToSpend;
    const isOverMinimum = creditsNeeded > 0 && creditsToSpend > creditsNeeded;
    const alreadyCovered = creditsNeeded === 0;

    const summary = useMemo(() => {
        if (alreadyCovered) {
            return (
                <>
                    This {subjectLabel} already has enough unlocked runs. You can still spend credits to unlock
                    additional capacity for future upgrades.
                </>
            );
        }

        return (
            <>
                You need at least <strong>{formatCount(creditsNeeded)}</strong> credit
                {creditsNeeded === 1 ? "" : "s"} to cover the remaining <strong>{formatCount(shortfallRuns)}</strong>{" "}
                runs for this {subjectLabel}.
            </>
        );
    }, [alreadyCovered, creditsNeeded, shortfallRuns]);

    const handleSpend = async () => {
        try {
            setIsSpending(true);
            setSpendError(null);
            await dispatch(
                addTransactionToHistory({
                    type: transactionType,
                    credits: creditsToSpend,
                    spentDetails: {
                        feature: spentFeature,
                        url: spentUrl,
                        id: spentId,
                        name: spentName,
                    },
                })
            );
            mixpanel.track(`${analyticsLabel} Runs Unlock`, {
                UserId: userUid,
                AnalysisId: spentId,
                Credits: creditsToSpend,
                CreditType: "new",
                Runs: runsToUnlock,
            });
            onClose();
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || error?.payload?.message || "Failed to unlock runs";
            setSpendError(message);
        } finally {
            setIsSpending(false);
        }
    };

    return (
        <div className={classes.newCreditsContainer}>
            <div className={classes.body}>
                <p className={classes.summaryText}>{summary}</p>
                <p className={classes.rateText}>
                    Credits unlock runs in batches of <strong>{formatCount(runsPerCredit)}</strong> (1 credit ={" "}
                    {formatCount(runsPerCredit)} runs).
                </p>

                <div className={classes.stepperRow}>
                    <div className={classes.stepperLabel}>
                        <span>Credits to spend</span>
                        <strong>
                            {creditsToSpend} batch{creditsToSpend === 1 ? "" : "es"} · +
                            {formatCount(runsToUnlock)} runs → {formatCount(projectedUnlocked)} unlocked
                        </strong>
                    </div>
                    <div className={classes.stepperControls}>
                        <button
                            type="button"
                            className={classes.stepperButton}
                            aria-label="Decrease credits"
                            disabled={creditsToSpend <= minCredits || isSpending}
                            onClick={() => setCreditsToSpend((value) => Math.max(minCredits, value - 1))}
                        >
                            −
                        </button>
                        <span className={classes.stepperValue}>{creditsToSpend}</span>
                        <button
                            type="button"
                            className={classes.stepperButton}
                            aria-label="Increase credits"
                            disabled={isSpending}
                            onClick={() => setCreditsToSpend((value) => value + 1)}
                        >
                            +
                        </button>
                    </div>
                </div>

                {isOverMinimum && (
                    <p className={`${parentClasses.alert} ${parentClasses.alertWarning}`}>
                        You&apos;re unlocking more runs than this {subjectLabel} currently needs. That&apos;s fine if you plan
                        to add more upgrades later.
                    </p>
                )}

                {!hasEnoughCredits && (
                    <p className={`${parentClasses.alert} ${parentClasses.alertError}`}>
                        You need <strong>{formatCount(creditsToSpend)}</strong> credit
                        {creditsToSpend === 1 ? "" : "s"} to unlock these runs, but you only have{" "}
                        <strong>{formatCount(newTotalCredits)}</strong>.
                    </p>
                )}

                {spendError && <p className={classes.errorText}>{spendError}</p>}
            </div>

            <div className={classes.footer}>
                <Button type="red" onClick={onClose} disabled={isSpending}>
                    Cancel
                </Button>
                <Button onClick={handleSpend} disabled={!hasEnoughCredits || isSpending || creditsToSpend <= 0}>
                    {isSpending
                        ? "Unlocking..."
                        : `Spend ${creditsToSpend} credit${creditsToSpend === 1 ? "" : "s"}`}
                </Button>
            </div>
        </div>
    );
};

export default NewCredits;
